/**
 * The signed-out screen — every way in and out of this application that is not signed in.
 *
 * It is deliberately a *screen* and not a banner over a half-drawn Control Centre: a 401
 * means nothing on the page behind it can be trusted to be the caller's own data, and a
 * broken panel that still shows its chrome invites a reload rather than an explanation.
 *
 * **What is not here, and why it is not a gap.**
 *  · No password field. There are no passwords in this system, for anyone (spec 09 §1).
 *  · No sign-up. The users are the TD and six Umpires Managers; there is no self-registration
 *    (decision D-3), so a `Create Account` control would be a door onto a wall.
 *  · **No email form and no `Send Me A New Link` button.** This application never sends mail
 *    (D19/D20): the Control Centre generates links and the Technical Delegate sends them from
 *    his own mailbox. A button that appeared to send one would be a lie, and a form that
 *    accepted an address and did nothing would be worse. The copy says where a link comes
 *    from instead. Spec 09 §3.1/§3.3 draw those controls; the deviation is deliberate and
 *    recorded.
 */

import type { SignedOutReason } from "../app/session.js";

interface Opening {
  title: string;
  detail: string;
  /**
   * RUN 87 - for a device that has never been signed in, and the one reason a link may have been
   * opened here without being used: opening a link no longer spends it, only `Continue To Sign In`
   * does, and the token is held in memory only - so a reload after the link was opened lands here
   * with no link, and the person must be told the link they have still works. It is true of the
   * server as well as the screen: a link is spent only by a sign-in that succeeds, and before that
   * it stops working only if a newer one is issued for the same person (which is why the most
   * recent is the one to use), the Technical Delegate withdraws their access, or its year runs out.
   * Not said for `expired` or `signed_out`: the link that signed that device in has been used.
   */
  again?: string;
}

const OPENING: Readonly<Record<SignedOutReason, Opening>> = {
  no_credential: {
    title: "This Device Is Not Signed In",
    detail:
      "Sign-in is by a personal link. Open the link the Technical Delegate sent you, on the " +
      "device you will use during the tournament.",
    again:
      "Opened your link before? Open it again from your message: it still works until it is " +
      "used to sign in. If you were sent more than one, use the most recent.",
  },
  expired: {
    title: "Your Access Has Expired",
    detail:
      "The credential this device was holding is no longer valid — it has either lapsed or " +
      "been withdrawn. Nothing you have already sent is affected.",
  },
  signed_out: {
    title: "You Are Signed Out",
    detail: "This device no longer holds a credential. Nothing else on it has changed.",
  },
};

export function SignedOutPage({ reason }: { reason: SignedOutReason }): React.JSX.Element {
  const opening = OPENING[reason];

  return (
    <div className="signedout">
      <section className="panel panel--d1 signedout__panel" aria-labelledby="signedOutTitle">
        <div className="panel__head">
          <h3 className="panel__title" id="signedOutTitle">
            {opening.title}
          </h3>
          {/* The application, not the event. This screen is drawn for a device with no
              credential, so there is no authenticated read to learn the tournament from — and
              the fixed `Asian Games 2026` it used to carry was wrong on every other
              installation. The chrome names the tournament once a session exists. */}
          <span className="panel__note">TD Control Centre And UM Feedback App</span>
        </div>

        <div className="emptystate">
          <b>{opening.detail}</b>
          {opening.again === undefined ? null : (
            <span className="signedout__again">{opening.again}</span>
          )}
          There is no password on this system, for anyone, and there is nothing to sign up
          for.
        </div>

        <div className="panel__foot">
          <b>How to get a link.</b> Ask the Technical Delegate to issue you one. This screen
          cannot send it: the Control Centre never sends email — it produces the link and the
          Technical Delegate passes it to you directly. A link works once.
        </div>
      </section>
    </div>
  );
}
